import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody } from "../Common/AppOverlay";
import "./FindingWorkspaceModal.scss";

const dateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const dateOnly = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const initials = (value) =>
  String(value || "User")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const displayStatus = (value) =>
  ({ InProgress: "In Progress", InReview: "In Review", ReQuery: "Re-query" }[
    value
  ] ||
  value ||
  "Open");

const messageLabel = (type) =>
  ({
    Created: "Finding raised",
    Response: "Response submitted",
    ReQuery: "Re-query raised",
    Resolved: "Marked as resolved",
    Reopened: "Finding reopened",
    Closed: "No further action required",
    Status: "Status updated",
  }[type] || "Comment");

const FindingWorkspaceModal = ({
  finding: sourceFinding,
  readOnly = false,
  assigneeName,
  onClose,
  onEdit,
  onPostComment,
  onTransition,
  onSubmitResponse,
  onDownload,
  saving = false,
}) => {
  const finding = readOnly && sourceFinding ? { ...sourceFinding, canReview: false, canStartWork: false, canSubmitForReview: false } : sourceFinding;
  const [tab, setTab] = useState("discussion");
  const [draft, setDraft] = useState("");
  useEffect(() => {
    setTab("discussion");
    setDraft("");
  }, [finding?.id]);
  const messages = useMemo(() => finding?.messages || [], [finding]);
  if (!finding) return null;

  const status = finding.findingStatus || "Open";
  const isTerminal = status === "Resolved" || status === "Closed";
  const canRequestClarification = finding.canReview && status === "InReview";
  const canResolve = finding.canReview && status === "InReview";
  const canCloseNoAction =
    finding.canReview && !finding.managementResponseRequired && !isTerminal;

  const runTransition = async (action) => {
    const requiresNote = [
      "requestclarification",
      "resolve",
      "reopen",
      "closenothingrequired",
    ].includes(action);
    if (requiresNote && !draft.trim()) return;
    if ((await onTransition(action, draft.trim())) !== false) setDraft("");
  };

  const postComment = async () => {
    if (readOnly) return;
    if (!draft.trim()) return;
    if ((await onPostComment(draft.trim())) !== false) setDraft("");
  };

  const workflow = [
    ["Finding raised", true],
    [
      finding.managementResponseRequired ? "Assigned" : "No action required",
      true,
    ],
    [
      "In progress",
      ["InProgress", "ReQuery", "InReview", "Resolved"].includes(status),
    ],
    ["Ready for review", ["InReview", "Resolved"].includes(status)],
    [status === "Closed" ? "Closed" : "Resolved", isTerminal],
  ];

  return (
    <Modal
      isOpen
      centered
      size="xl"
      toggle={() => !saving && onClose()}
      className="finding-workspace-modal"
    >
      <header className="finding-workspace__header">
        <div>
          <i className="bx bx-flag" />
          <span>
            <h2>{finding.findingNumber}</h2>
            <p>{finding.title}</p>
          </span>
        </div>
        <span
          className={`finding-workspace__severity is-${String(
            finding.severity || "low"
          ).toLowerCase()}`}
        >
          <i className="bx bxs-flag-alt" /> {finding.severity}
        </span>
        <span
          className={`finding-workspace__status is-${String(
            status
          ).toLowerCase()}`}
        >
          {displayStatus(status)}
        </span>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          aria-label="Close finding"
        >
          <i className="bx bx-x" />
        </button>
      </header>
      <section className="finding-workspace__summary">
        <div>
          <small>Checklist / Question</small>
          <b>{finding.questionCode || "Current checklist question"}</b>
        </div>
        <div>
          <small>Assigned to</small>
          <b>
            {assigneeName ||
              finding.responseRequestedFromRole ||
              "No action required"}
          </b>
        </div>
        <div>
          <small>Raised</small>
          <b>{dateOnly(finding.createdOn)}</b>
        </div>
        <div>
          <small>Due date</small>
          <b className="is-due">{dateOnly(finding.responseDueDate)}</b>
        </div>
      </section>
      <nav className="finding-workspace__tabs" aria-label="Finding sections">
        {[
          ["details", "Details"],
          ["discussion", `Discussion (${messages.length})`],
          ["resolution", "Resolution Plan"],
          ["evidence", `Evidence (${finding.evidence?.length || 0})`],
          ["history", "History"],
        ].map(([id, label]) => (
          <button
            type="button"
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      <ModalBody className="finding-workspace__body">
        <main>
          {finding.detailsLoading ? (
            <div className="finding-workspace__loading" role="status">
              <i className="bx bx-loader-alt bx-spin" /> Loading finding
              details…
            </div>
          ) : (
            <>
              {tab === "details" && (
                <section className="finding-workspace__panel finding-workspace__details">
                  <header>
                    <i className="bx bx-file" />
                    <b>Finding details</b>
                    {!readOnly && onEdit && (
                      <button type="button" onClick={onEdit}>
                        <i className="bx bx-edit-alt" /> Edit
                      </button>
                    )}
                  </header>
                  <dl>
                    {finding.deficiencyExists != null && <div><dt>Deficiency exists?</dt><dd>{finding.deficiencyExists ? "Yes" : "No"}</dd></div>}
                    {finding.deficiencyExists === true && <>
                      <div><dt>Theme</dt><dd>{finding.theme || "—"}</dd></div>
                      <div><dt>Severe / Pervasive</dt><dd>{finding.isSevere ? "Yes" : "No"} / {finding.isPervasive ? "Yes" : "No"}</dd></div>
                      <div className="is-wide"><dt>Remediation action plan</dt><dd>{finding.remediationActionPlan || "—"}</dd></div>
                      <div><dt>Remedial actions taken and effective?</dt><dd>{finding.remedialActionsEffective ? "Yes" : "No"}</dd></div>
                      {finding.remedialActionsEffective && <div><dt>Remedial actions date</dt><dd>{dateOnly(finding.remedialActionsDate)}</dd></div>}
                      <div className="is-wide"><dt>Remedial action comments</dt><dd>{finding.remedialActionsComments || "—"}</dd></div>
                      <div><dt>Finding resolved?</dt><dd>{finding.findingResolved ? "Yes" : "No"}</dd></div>
                      {finding.findingResolved && <div><dt>Finding resolved date</dt><dd>{dateOnly(finding.findingResolvedDate)}</dd></div>}
                      <div className="is-wide"><dt>Resolution comments</dt><dd>{finding.findingResolutionComments || "—"}</dd></div>
                    </>}
                    <div>
                      <dt>Title</dt>
                      <dd>{finding.title}</dd>
                    </div>
                    <div>
                      <dt>Category</dt>
                      <dd>{finding.category || "—"}</dd>
                    </div>
                    <div>
                      <dt>Severity</dt>
                      <dd>{finding.severity || "—"}</dd>
                    </div>
                    <div className="is-wide">
                      <dt>Description</dt>
                      <dd>{finding.description || "—"}</dd>
                    </div>
                  </dl>
                </section>
              )}
              {tab === "resolution" && (
                <section className="finding-workspace__panel finding-workspace__details">
                  <header>
                    <i className="bx bx-target-lock" />
                    <b>Resolution plan</b>
                  </header>
                  <dl>
                    <div className="is-wide">
                      <dt>Expected action / Recommendation</dt>
                      <dd>
                        {finding.recommendation ||
                          "No recommendation recorded."}
                      </dd>
                    </div>
                    <div className="is-wide">
                      <dt>Root cause</dt>
                      <dd>{finding.rootCause || "Not yet recorded."}</dd>
                    </div>
                    <div>
                      <dt>Resolution owner</dt>
                      <dd>
                        {assigneeName ||
                          finding.responseRequestedFromRole ||
                          "—"}
                      </dd>
                    </div>
                    <div>
                      <dt>Target date</dt>
                      <dd>{dateOnly(finding.responseDueDate)}</dd>
                    </div>
                  </dl>
                </section>
              )}
              {tab === "evidence" && (
                <section className="finding-workspace__panel">
                  <header>
                    <i className="bx bx-paperclip" />
                    <b>Supporting evidence</b>
                  </header>
                  <div className="finding-workspace__files">
                    {finding.evidence?.length ? (
                      finding.evidence.map((file) => (
                        <article key={file.id || file.inspectionDocumentId}>
                          <i className="bx bx-file" />
                          <span>
                            <b>{file.fileName || file.name}</b>
                            <small>
                              {Math.max(
                                1,
                                Math.round(Number(file.sizeBytes || 0) / 1024)
                              )}{" "}
                              KB · {dateOnly(file.createdOn)}
                            </small>
                          </span>
                          {onDownload && (
                            <button
                              type="button"
                              onClick={() => onDownload(file)}
                            >
                              <i className="bx bx-download" />
                            </button>
                          )}
                        </article>
                      ))
                    ) : (
                      <p>No supporting evidence has been attached.</p>
                    )}
                  </div>
                </section>
              )}
              {tab === "history" && (
                <section className="finding-workspace__panel">
                  <header>
                    <i className="bx bx-history" />
                    <b>Workflow history</b>
                  </header>
                  <div className="finding-workspace__history">
                    {messages
                      .filter((message) => message.type !== "Comment")
                      .map((message) => (
                        <article key={message.id}>
                          <i className="bx bx-check-circle" />
                          <span>
                            <b>{messageLabel(message.type)}</b>
                            <p>{message.text}</p>
                            <small>
                              {message.authorName} ·{" "}
                              {dateTime(message.createdOn)}
                            </small>
                          </span>
                        </article>
                      ))}
                  </div>
                </section>
              )}
              {tab === "discussion" && (
                <section className="finding-workspace__conversation">
                  {messages.length ? (
                    messages.map((message) => (
                      <article
                        key={message.id}
                        className={`is-${String(
                          message.type || "comment"
                        ).toLowerCase()}`}
                      >
                        <span>{initials(message.authorName)}</span>
                        <div>
                          <header>
                            <b>{message.authorName || "Review participant"}</b>
                            <em>{messageLabel(message.type)}</em>
                            <time>{dateTime(message.createdOn)}</time>
                          </header>
                          <p>{message.text}</p>
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="finding-workspace__empty">
                      No discussion has been recorded.
                    </p>
                  )}
                </section>
              )}
            </>
          )}
        </main>
        <aside>
          <section className="finding-workspace__owner">
            <header>
              <i className="bx bx-user" />
              <b>Resolution owner</b>
            </header>
            <div>
              <span>{initials(assigneeName)}</span>
              <p>
                <b>{assigneeName || "Not assigned"}</b>
                <small>
                  {finding.responseRequestedFromRole ||
                    (finding.managementResponseRequired
                      ? "Assigned user"
                      : "No action required")}
                </small>
              </p>
            </div>
          </section>
          <section className="finding-workspace__workflow">
            <header>
              <i className="bx bx-git-branch" />
              <b>Resolution workflow</b>
            </header>
            {workflow.map(([label, complete], index) => (
              <div className={complete ? "is-complete" : ""} key={label}>
                <i className={`bx ${complete ? "bx-check" : "bx-circle"}`} />
                <span>
                  <b>{label}</b>
                  {index === 2 && status === "ReQuery" && (
                    <small>Clarification requested</small>
                  )}
                </span>
              </div>
            ))}
          </section>
        </aside>
        {!readOnly && <section className="finding-workspace__composer">
          <textarea
            rows="3"
            maxLength="4000"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Add a comment, resolution note, or clarification request…"
          />
          <div>
            <small>
              {draft.length}/4000 · Posting a comment does not change status.
            </small>
            <button
              type="button"
              onClick={postComment}
              disabled={saving || !draft.trim()}
            >
              <i className="bx bx-send" /> Post Comment
            </button>
          </div>
        </section>}
      </ModalBody>
      <footer className="finding-workspace__actions">
        <button type="button" onClick={onClose} disabled={saving}>
          Close
        </button>
        {finding.canStartWork && (
          <button
            type="button"
            onClick={() => runTransition("startwork")}
            disabled={saving}
          >
            <i className="bx bx-play" /> Start Work
          </button>
        )}
        {finding.canSubmitForReview &&
          !["InReview", "Resolved", "Closed"].includes(status) && (
            <button
              type="button"
              className="is-primary"
              onClick={onSubmitResponse}
              disabled={saving}
            >
              <i className="bx bx-check" /> Submit for Review
            </button>
          )}
        {canRequestClarification && (
          <button
            type="button"
            onClick={() => runTransition("requestclarification")}
            disabled={saving || !draft.trim()}
          >
            <i className="bx bx-refresh" /> Request Clarification
          </button>
        )}
        {canResolve && (
          <button
            type="button"
            className="is-primary"
            onClick={() => runTransition("resolve")}
            disabled={saving || !draft.trim()}
          >
            <i className="bx bx-check-circle" /> Resolve Finding
          </button>
        )}
        {canCloseNoAction && (
          <button
            type="button"
            className="is-primary"
            onClick={() => runTransition("closenothingrequired")}
            disabled={saving || !draft.trim()}
          >
            <i className="bx bx-check" /> Close — No Action
          </button>
        )}
        {finding.canReview && isTerminal && (
          <button
            type="button"
            onClick={() => runTransition("reopen")}
            disabled={saving || !draft.trim()}
          >
            <i className="bx bx-undo" /> Reopen Finding
          </button>
        )}
      </footer>
    </Modal>
  );
};

export default FindingWorkspaceModal;
